import { Datagrid } from "ui-kit/src/Datagrid/Datagrid";
import { useState, useRef } from "react";
import { useToast } from "ui-kit";
import { MapModal } from "./MapModal";
import { BaseModal } from "./BaseModal";
import { useModal } from "../../features/modalProvider";
import { _ } from "i18n";
import { DieLigenApiManagementConstants } from "../../pages/productManagement/apiManagement/dieLigen/DieLigenApiManagementConstants";
import { useTeamsheet } from "../../hooks/apiManagement/useTeamsheet";
import { headerRenderers, createCellRenderers } from "./TeamsheetTableRenderers";
import { 
    createNonPartnerPlayerData,
    createPartnerPlayerData,
} from "../../helpers/apiManagement/teamsheetHelpers";
import { TeamsheetModalProps, TeamsheetPlayerData } from "types/apiManagement";
import { useUpdatePlayerStatus } from "../../hooks/apiManagement/useUpdatePlayerStatus";
import client from "libs/client";
import { WretchError } from "wretch";
import { HttpStatus } from '../../../../../e2e/enums/enums';
import { useClubList } from "../../hooks/apiManagement/useClubList";
import { APIResponse } from "../../types";

type DataResponse = {
    data: {
        status: string | number;
        message: string;
        data: Record<string, any>;
    };
} & {
    status?: string | number;
    message?: string;
};

export const TeamsheetModal: React.FC<TeamsheetModalProps> = ({ 
    isOpen, 
    onClose,
    homeTeam,
    awayTeam,
    taskId,
    homeTeamId,
    awayTeamId,
}) => {
    const [showMapModal, setShowMapModal] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState<TeamsheetPlayerData | null>(null);
    const { addErrorToast, addSuccessToast } = useToast();
    const { transformedTeamsheet, isTeamsheetLoading } = useTeamsheet(taskId);
    const { mutation: handleUpdateStatus } = useUpdatePlayerStatus();
    const { showFeedback } = useModal();
    const { findSimilarClubs } = useClubList();
    const currentPlayerRef = useRef<TeamsheetPlayerData | null>(null);

    const handlePlayerSuccess = (
        data: DataResponse, 
        player: TeamsheetPlayerData,
    ) => {
        const isPartnerSuccess = data.data?.status === HttpStatus.Ok;
        const isNonPartnerSuccess = data.data?.status === APIResponse.SUCCESS || data.status === APIResponse.SUCCESS;
        
        if (!isPartnerSuccess && !isNonPartnerSuccess) {
            const errorMessage = data.data?.message || data.message || _("video-tasks.modal.teamsheet.error.unknown");
            addErrorToast(errorMessage);
            return;
        }
        
        const updateData = {
            id: Number.parseInt(player.id),
            body: {
                eyeballPlayerId: data.data.data.id,
                status: DieLigenApiManagementConstants.Status.MAPPED
            }
        };

        handleUpdateStatus.mutate(updateData);
        showFeedback(_("video-tasks.modal.teamsheet.success.mapped"), "success");
        addSuccessToast(_("video-tasks.modal.teamsheet.createStatus.success"));
    };


    const createPlayerMutation = client.createPlayer.useMutation({
        onSuccess: (data) => {
            const player = currentPlayerRef.current;
            if (player) {
                handlePlayerSuccess(
                    data as DataResponse, 
                    player,
                );
            }
        },
        onError: (error: WretchError) => {
            const errorMessage = error?.json?.message || _("video-tasks.modal.teamsheet.error.unknown");
            addErrorToast(errorMessage);
        },
    });

    const handleCreate = async (player: TeamsheetPlayerData) => {
        const teamId = player.data.provider.team === "home" ? Number(homeTeamId) : Number(awayTeamId);

        if (!teamId) {
            addErrorToast(_("video-tasks.modal.teamsheet.error.no_club"));
            return;
        }

        const nonPartnerPlayerData = createNonPartnerPlayerData(player, findSimilarClubs, homeTeam, awayTeam);
        const partnerPlayerData = createPartnerPlayerData(player, teamId);

        currentPlayerRef.current = player;

        createPlayerMutation.mutate({
            id: teamId,
            body: {
                player: partnerPlayerData,
                nonPartnerPlayerData: nonPartnerPlayerData,
            }
        });       
    };

    const handleMap = (value: string) => {
        if (!Number.isInteger(Number(value))) {
            addErrorToast(_("video-tasks.modal.teamsheet.error.must_be_integer"));
            return;
        }
        
        const updateData = {
            id: Number.parseInt(selectedPlayer?.id || ""),
            body: {
                eyeballPlayerId: Number.parseInt(value),
                status: DieLigenApiManagementConstants.Status.MAPPED
            }
        };

        handleUpdateStatus.mutate(updateData);
    };

    const allPlayers = [
        ...transformedTeamsheet.homeTeamPlayers,
        ...transformedTeamsheet.awayTeamPlayers
    ];

    const cellRenderers = createCellRenderers(
        handleCreate,
        (player) => {
            setSelectedPlayer(player);
            setShowMapModal(true);
        },
    );

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                title={_("video-tasks.modal.teamsheet.title")}
                size="sm"
            >
                <div className="datatable-container modal-datatable-container">
                    <Datagrid
                        className="table"
                        data={allPlayers}
                        columns={DieLigenApiManagementConstants.teamsheetColumns as any}
                        headerRenderers={headerRenderers}
                        cellRenderers={cellRenderers}
                        override={true}
                        pageSize={30}
                        initCurrentPage={0}
                        showLoader={isTeamsheetLoading}
                    />
                </div>
            </BaseModal>
            <MapModal 
                isOpen={showMapModal}
                onClose={() => {
                    setShowMapModal(false);
                    setSelectedPlayer(null);
                }}
                onSubmit={handleMap}
                isLoading={handleUpdateStatus.isLoading}
            />
        </>
    );
} 